import { $t } from "@/plugins/i18n"

import { Base } from "./Base"

export const aiGenders = ["MALE", "FEMALE", "EITHER"]

export const aiContextTypes = [
  "GENERAL",
  "SPRINT_ANALYTICS",
  "SPRINT_REVIEW",
  "AI_INSIGHTS_ON_SPRINT_REVIEW",
  "AI_RETROSPECTIVE_INSIGHTS",
  "ITEM_ACCEPTANCE_CRITERIA",
  "ITEM_STATUS_CHANGE_COMMENT"
] as const

export type AiGender = (typeof aiGenders)[number]

export type AiContextType = (typeof aiContextTypes)[number]

export class ConversationParams {
  contextType: AiContextType = "GENERAL"
  contextEntityId = ""
  projectId = ""

  constructor(props?: Partial<ConversationParams>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export interface AiStructuredContent {
  html: string
  text: string
}

export interface ChatGPTItem {
  id: string
  mine: boolean
  content: string
}

export interface ChatGPTResponse {
  content: string
  structuredContent?: AiStructuredContent
  tokensLeft: number
}

export interface ItemAcceptanceCriteriaRequest {
  title: string
  description: string
}

/** Metadata for files persisted with an AI message. */
export interface AiMessageAttachment {
  id: string
  name: string
  size: number
  mediaType?: string
}

export class AiConversationMessage {
  content = ""
  createdDate = ""
  id = ""
  ordinal = 0
  role = ""
  attachments: AiMessageAttachment[] = []
  notSent = false
  /** Why the message was not sent, shown next to it instead of a global alert. */
  errorMessage = ""

  constructor(props?: Partial<AiConversationMessage>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.attachments = props.attachments || []
  }
}

/** Identifies the page entities available for one AI question. */
export interface AiPageContext {
  projectId?: string
  teamIds?: string[]
  itemId?: string
  ideaId?: string
  sprintId?: string
  retrospectiveId?: string
  epicId?: string
}

/** Carries one question and its page context to the AI message endpoint. */
export class SendAiMessageRequest {
  message = ""
  context?: AiPageContext
  attachmentIds?: string[]
  clientMessageId?: string

  constructor(props?: Partial<SendAiMessageRequest>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AiConversationMessageResponse {
  assistantMessage = new AiConversationMessage()
  conversationModifiedDate = ""
  userMessage = new AiConversationMessage()

  constructor(props?: Partial<AiConversationMessageResponse>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.assistantMessage = new AiConversationMessage(props.assistantMessage)
    this.userMessage = new AiConversationMessage(props.userMessage)
  }
}

export class AiConversations {
  accountId = ""
  contextEntityId = ""
  contextType: AiContextType = "GENERAL"
  createdDate = ""
  id = ""
  messages: AiConversationMessage[] = []
  modifiedDate = ""
  title = ""
  userId = ""

  constructor(props?: Partial<AiConversations>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.messages = props.messages
      ? props.messages.map(message => new AiConversationMessage(message))
      : []
  }
}

export class SprintAnalyticsInsightRequest {
  maxOutputTokens = 2500
  model?: string
  regenerate = true
  systemPrompt?: string
  temperature?: number

  constructor(props?: Partial<SprintAnalyticsInsightRequest>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AIConfigCurrent extends Base<AIConfigCurrent> {
  accountId = ""
  apiKey = ""
  id = ""
  model: AIConfigModels = new AIConfigModels()
  provider = ""
  providerTitle = ""
  name = ""
  picture = {
    pictureUrl: ""
  }
  gender: AiGender = ""

  constructor(props?: Partial<AIConfigCurrent>) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.providerTitle = this.provider ? $t(`aiConfigProvider.${this.provider}`) : ""
    this.model = new AIConfigModels(this.model)
    this.picture = { ...this.picture }
  }

  getJsonObj() {
    return {
      provider: this.provider,
      model: this.model,
      apiKey: this.apiKey,
      name: this.name,
      picture: this.picture,
      gender: this.gender
    }
  }
}

export class AIConfigModels {
  model = ""
  name = ""
  order = 0
  title = ""
  enabled = true

  constructor(props?: Partial<AIConfigModels>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    return {
      name: this.name
    }
  }

  static isSelectDisabled(models: AIConfigModels[], selected?: AIConfigModels | null): boolean {
    if (!models.length) return true

    return models.length === 1 && models[0].name === selected?.name
  }
}

export class AIConfigProvider {
  provider = ""
  providerTitle = ""
  models: AIConfigModels[] = []

  constructor(props?: Partial<AIConfigProvider>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.providerTitle = this.provider ? $t(`aiConfigProvider.${this.provider}`) : ""
    this.models = props.models?.map(model => new AIConfigModels(model)) ?? []
  }
}

export class AIConfig {
  current: AIConfigCurrent = new AIConfigCurrent()
  providers: AIConfigProvider[] = []

  constructor(props?: Partial<AIConfig>) {
    if (!props) return
    this.current = new AIConfigCurrent(props.current)
    this.providers = props.providers ? props.providers.map(p => new AIConfigProvider(p)) : []
  }
}

export class AIPromptSimplify {
  id = ""
  name = ""
  projectId = ""
  enabled = true

  constructor(props?: Partial<AIPromptSimplify>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}

export class AIPromptDetail extends AIPromptSimplify {
  defaultPrompt = true
  exampleInputJson = ""
  maxOutputTokens = 0
  temperature = 0
  prompt = ""
  id = ""
  systemPrompt: string = ""

  constructor(props?: Partial<AIPromptDetail>) {
    super(props)
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    return {
      prompt: this.prompt,
      systemPrompt: this.systemPrompt,
      exampleInputJson: this.exampleInputJson
    }
  }
}

export class AIProfile {
  gender: AiGender = ""
  picture = {
    pictureUrl: ""
  }
  name = ""
  provider = ""

  constructor(props?: Partial<AIProfile>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }
}
